import React from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardStackedBarChart from '@components/pages/Dashboard/MetricCharts/DashboardStackedBarChart.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { UseCollectionResult } from '@hooks/dashboard/useCollection'
import { COLLECTION_PERFORMANCE_SERIES } from '@constants/Dashboard/financeDashboard.constants'
import {
  financeTrendLine,
  formatChartCurrencyKUnits,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  FinanceCollectionMetricsTiles,
  FinanceGrossNetBarLegend,
  FinanceProductionSectionHeaderRight,
  FINANCE_DUAL_BAR_CHART_PROPS,
} from '../../FinanceMetricTiles.component'

type CollectionPerformanceCardProps = {
  titleHref: string
  range: RangeKey
  collection: UseCollectionResult
}

export function CollectionPerformanceCard({
  titleHref,
  range,
  collection,
}: CollectionPerformanceCardProps): React.ReactElement {
  const {
    loading,
    grossStr,
    netStr,
    adjustmentStr,
    rateStr,
    changeSignedPct,
    changeAmt,
    grossNetChart,
    collGoalLabel,
    collGoalLine,
    hasPayload,
    hasGrossData,
    hasNetData,
    hasRateData,
    hasTrendData,
  } = collection
  const isEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasGrossData || hasNetData || hasRateData || hasTrendData,
  )
  const isChartEmpty = shouldShowDashboardSectionEmpty(loading, hasTrendData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <DashboardSectionCard
      title="Collection performance"
      titleHref={titleHref}
      className="dashboard-finance-collection-perf-card"
      loading={pending}
      skeleton={{ metrics: 4, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
      headerRight={
        <FinanceProductionSectionHeaderRight
          trendLine={
            loading
              ? undefined
              : financeTrendLine(range, changeSignedPct, changeAmt)
          }
          goalLabel={collGoalLabel}
        />
      }
    >
      <div className="dashboard-finance-production">
        <FinanceCollectionMetricsTiles
          prodColl={{
            gross: loading ? HYGIENE_PLACEHOLDER : grossStr,
            net: loading ? HYGIENE_PLACEHOLDER : netStr,
            adjustment: loading ? HYGIENE_PLACEHOLDER : adjustmentStr,
            rate: loading ? HYGIENE_PLACEHOLDER : rateStr,
          }}
        />
        {isChartEmpty ? (
          <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
        ) : (
          <div className="dashboard-finance-production-chart-wrap dashboard-finance-bar-chart-legend-spaced">
            <DashboardStackedBarChart
              data={loading ? [] : grossNetChart.data}
              series={[...COLLECTION_PERFORMANCE_SERIES]}
              horizontal={false}
              stacked={false}
              valueFormatter={formatChartCurrencyKUnits}
              height={220}
              yAxisDomain={grossNetChart.yDomain}
              yAxisTicks={grossNetChart.yTicks}
              goalLineValue={collGoalLine}
              showLegend={false}
              className="dashboard-finance-production-bar-chart"
              {...FINANCE_DUAL_BAR_CHART_PROPS}
            />
            <FinanceGrossNetBarLegend />
          </div>
        )}
      </div>
    </DashboardSectionCard>
  )
}
